import { Vue } from "vue-property-decorator";
import lodash from "lodash";
import * as excel from "@/util/excel";
import {
  TransactionItem,
  Field,
  DataItem,
  ExcelRow,
  TxnStatusType,
  TableData,
  FilterData
} from "./types/transactions_query";
import * as backend from "./backend";
import * as util from "../util";
import { CardTypeCode, CARD_TYPES, Transaction } from "./types";
import { DataTableHeader } from "vuetify";
import { makeSet, Set } from "../util";
import store from "../domain/store";

const lang = store.state.lang.data;

export async function exportTransactionsExcel(
  filteredTransactions: TransactionItem[],
  fieldDescriptors: Field[],
  fileData: { companyName: string; branchName: string; from: string; to: string }
): Promise<void> {
  const fields = lodash(fieldDescriptors)
    .filter((fld) => !!fld.excel)
    .map((fld) => ({
      title: fld.title,
      field: fld.field,
      format: fld.format,
      excel: fld.excel
    }))
    .sortBy((fld) => fld.excel?.order)
    .value();
  const headers = fields.map((fld) => excel.header(fld.title, fld.field, fld.excel?.width || 15, fld.excel?.format));
  const data = filteredTransactions.map((txn) => mapExcelRow(fields, txn));
  const filename = backend.getFileName(
    "listing",
    fileData.companyName,
    fileData.branchName,
    fileData.from,
    fileData.to
  );
  return excel.writeSpreadsheet(filename, "Transacciones", headers, data);
}

function mapExcelRow(fields: Field[], txn: DataItem) {
  const result: ExcelRow = {};
  fields.forEach((field) => {
    if (field.field) {
      const value = excelFormat(txn, field);
      if (value) {
        result[field.field] = value;
      }
    }
  });
  return result;
}

function excelFormat(item: DataItem, field: Field): number | string {
  if (field.excel && (field.excel.format === "number" || field.excel.format === "money")) {
    return util.toNumberSafe(util.objProperty(item, field.field));
  }
  return fieldFormat(item, field);
}

export function fieldCSS(item: DataItem, field: Field) {
  if (field.details && field.details.cssClass) {
    if (typeof field.details.cssClass === "string") {
      return util.objProperty(item, field.details.cssClass);
    }
    return field.details.cssClass(item);
  }
  return "";
}

export function fieldPriceColor(item: DataItem | number | string | undefined): string {
  let value = 0;
  if (typeof item == "string" || typeof item == "number") {
    value = Number(item);
  } else if (item?.amountWithSign) {
    value = Number(item.amountWithSign);
  }
  return value < 0 ? "red--text" : "";
}

export function fieldFormat(item: DataItem, field: Field): string {
  if (field.field) {
    const value = util.objProperty(item, field.field);
    if (field.format && typeof field.format !== "string") {
      return field.format(value);
    } else if (field.format) {
      if (field.field === "totalCost" && value) return Number(value).toFixed(4).replace(".", ",");
      return Vue.filter(field.format)(value);
    } else {
      return value;
    }
  } else if (field.format && typeof field.format !== "string") {
    return field.format(item);
  }
  return "";
}

export function mapTransaction(t: Transaction): TransactionItem {
  let txnStatusType = txnStatuses.denied;
  if (util.toBool(t.failed)) {
    txnStatusType = txnStatuses.failed;
  } else if (util.toBool(t.approved)) {
    txnStatusType = txnStatuses.approved;
  }
  const result: TransactionItem = {
    ...t,
    isFailed: util.toBool(t.failed),
    isApproved: util.toBool(t.approved),
    veryHiddenCardNumber: getCardNumber(t),
    txnStatusType,
    statusType: txnStatusType.name
  };
  if (!result.cardTypeCode) result.cardTypeCode = "";
  if (util.toNumberSafe(t.amountWithSign) < 0) {
    result.requestedAmount = `-${t.requestedAmount}`;
  }
  return result;
}

function getCardNumber(item?: Transaction) {
  return (
    item?.cardNumbering?.plainCardNumber ?? item?.cardNumbering?.hiddenCardNumber ?? item?.veryHiddenCardNumber ?? ""
  );
}

export function getTableData<T>(selectAllOpTypes = false): TableData<T> {
  const result: TableData<T> = {
    headers: [],
    filters: {},
    data: []
  };
  const fields = getFieldDescriptors(selectAllOpTypes);
  result.headers = lodash(fields)
    .filter((fld) => !!fld.table)
    .sortBy((fld) => fld.table?.order || 0)
    .map((fld) => {
      const filterable = fld.table?.filterable !== false;
      const field: DataTableHeader = {
        text: fld.title,
        value: fld.table?.valueField || fld.field,
        sortable: fld.table?.sortType !== "none",
        divider: fld.table?.divider,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        sort: fld.table?.sortType === "number" ? (a: any, b: any) => util.sort.numbers(a, b) : undefined,
        width: fld.table?.width,
        align: fld.table?.align || "center",
        filterable
      };
      const filterCfg = fld.table?.filter;
      let filter: FilterData = {
        filterField: filterCfg?.filterField || fld.field,
        config: filterCfg,
        tNone: true,
        value: "",
        cachedValue: null
      };
      if (filterable && fld.table) {
        filter.tNone = false;
        filter = {
          ...filter,
          tSearch: !filterCfg?.list,
          tList: undefined
        };
        if (filterCfg?.list) {
          filter.tList = {
            multi: !!filterCfg.list.multi,
            showSearch: !!filterCfg.list.showSearch,
            options: [...(filterCfg.list.options || [])]
          };
          filter.value = [...(filterCfg.list?.values || [])];
        }
      }
      result.filters[field.value] = filter;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      field.filter = (_value: any, _search: string | null, item: any) => {
        return filterAny(result.filters[field.value], item);
      };
      return field;
    })
    .value();
  return result;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function filterAny(filter: FilterData, record: any): boolean {
  if (filter.tNone) {
    return true;
  }
  if (filter.tList) {
    const values = filter.cachedValue as Set;
    if (!values || values.size === 0) {
      return false;
    }
    const recordValue = util.objProperty(record, filter.filterField);
    return values.contains(recordValue);
  }
  if (filter.tSearch) {
    if (!filter.cachedValue) return true;
    const recordValue = util.objProperty(record, filter.filterField);
    if (!recordValue) return false;
    return (
      recordValue
        .toString()
        .toLocaleUpperCase()
        .indexOf(filter.cachedValue as string) >= 0
    );
  }
  return true;
}

export function setFilterValue(filter: FilterData, newValue: string | string[]) {
  filter.value = newValue;
  if (filter.filterField == "requestedAmount" || filter.filterField == "amountWithSign")
    filter.value = (newValue as string).replace(",", ".");
  if (Array.isArray(newValue)) filter.cachedValue = makeSet(newValue);
  else filter.cachedValue = (newValue || "").toLocaleUpperCase();
}

export function filterTableData<T>(tableData: TableData<T>, fullData: T[]) {
  if (util.isEmpty(fullData)) {
    tableData.data = fullData;
  } else {
    const fields = Object.values(tableData.filters);
    tableData.data = fullData.filter((item) => {
      return fields.every((filter) => {
        return filterAny(filter, item);
      });
    });
  }
}

export const txnStatuses: { [key: string]: TxnStatusType } = {
  approved: {
    value: "a",
    text: lang.approvedPlural,
    name: lang.approvedPlural,
    color: "success--text"
  },
  denied: {
    value: "d",
    text: lang.deniedPlural,
    name: lang.deniedPlural,
    color: "warning--text"
  },
  failed: {
    value: "f",
    text: lang.failedPlural,
    name: lang.failedPlural,
    color: "error--text"
  }
};

export function getFieldDescriptors(selectAllOpTypes = false): Field[] {
  const gral = lang.generalInfo;
  const come = lang.company;
  const card = lang.cardData;
  const proc = lang.processing;
  return [
    // Only on table
    {
      title: "·",
      field: "actions",
      table: { order: 1, sortType: "none", divider: false, filterable: false }
    },
    // Only in Excel
    {
      title: lang.id + "#",
      field: "id",
      excel: { order: 1 },
      table: { order: 2, align: "center", sortType: "number" }
    },
    {
      title: "Cod. Entrada",
      field: "dataEntryCode",
      excel: { order: 19 }
    },
    {
      title: "Modo de Entrada",
      field: "dataEntryText",
      excel: { order: 30 }
    },
    // Shared Properties
    {
      title: lang.office,
      field: "branchName",
      excel: { order: 2, width: 25 },
      details: { group: 1, category: gral, order: 2 },
      table: { order: 3, width: "100px", align: "start" }
    },
    {
      title: lang.dateTime,
      field: "timestamp",
      format: "dateTime",
      excel: { order: 3, width: 19 },
      details: { group: 1, category: gral, order: 3 },
      table: { order: 4, sortType: "number" }
    },
    {
      title: lang.operation,
      field: "operationTypeName",
      excel: { order: 8 },
      details: { group: 1, category: gral, order: 4 },
      table: {
        order: 5,
        filter: {
          filterField: "type",
          list: {
            multi: true,
            values: selectAllOpTypes
              ? ["sale", "saleAC", "refund", "refundAC", "auth", "authClose", "authCancel", "test", "token", "cancel"]
              : ["sale", "saleAC", "refund", "refundAC", "authClose"],
            options: [
              { value: "sale", text: lang.sale },
              { value: "saleAC", text: lang.forceSale },
              { value: "refund", text: lang.refund },
              { value: "refundAC", text: lang.forceRefund },
              { value: "auth", text: lang.preauthorization },
              { value: "authClose", text: lang.preauthorizationClose },
              { value: "authCancel", text: lang.preauthorizationCancelation },
              { value: "test", text: lang.amountTest },
              // { value: "cancel", text: "Cancelar" },
              // { value: "authTopup", text: "Top-Up Preaut." },
              { value: "token", text: lang.tokenization },
              { value: "cancel", text: lang.cancellation }
            ]
          }
        }
      }
    },
    {
      title: lang.requestedAmount,
      field: "requestedAmount",
      format: "money",
      excel: { order: 4, width: 17, format: "money" },
      details: { group: 1, category: gral, order: 5, cssClass: fieldPriceColor },
      table: { order: 6, align: "end", sortType: "number" }
    },
    {
      title: lang.finalAmount,
      field: "amountWithSign",
      format: "money",
      excel: { order: 5, format: "money" },
      details: { group: 1, category: gral, order: 6, cssClass: fieldPriceColor },
      table: { order: 12, align: "end", sortType: "number" }
    },
    {
      title: lang.authorizationCode,
      field: "authorisationCode",
      excel: { order: 7, width: 16 },
      details: { group: 1, category: gral, order: 7 },
      table: { order: 7, sortType: "number" }
    },
    {
      title: lang.transactionCost,
      field: "totalCost",
      format: "money",
      excel: { order: 28, width: 16 },
      details: { group: 1, category: gral, order: 8 }
    },
    {
      title: lang.state,
      field: "statusType",
      excel: { order: 11, width: 12 },
      details: {
        group: 1,
        category: gral,
        order: 9,
        cssClass: "txnStatusType.color"
      },
      table: {
        valueField: "txnStatusType",
        order: 8,
        filter: {
          filterField: ["txnStatusType", "value"],
          list: {
            multi: true,
            values: [txnStatuses.approved.value],
            options: [txnStatuses.approved, txnStatuses.denied, txnStatuses.failed]
          }
        }
      }
    },
    {
      title: lang.reference,
      field: "transactionReference",
      excel: { order: 9 },
      details: { group: 1, category: gral, order: 10 },
      table: { order: 11, align: "start" }
    },
    {
      title: lang.dcc + "?",
      field: "dcc",
      format: "boolSiNo",
      excel: { order: 17, width: 6 },
      details: { group: 1, category: gral, order: 11 },
      table: { order: 14 }
    },
    {
      title: lang.country,
      field: "country",
      excel: { order: 18, width: 6 },
      details: { group: 1, category: gral, order: 12 },
      table: { order: 15 }
    },
    // {
    //   title: lang.resultCode,
    //   field: "resultCode",
    //   excel: { order: 12, width: 19 },
    //   details: { group: 1, category: gral, order: 13 }
    // },
    {
      title: lang.result,
      field: "resultText",
      excel: { order: 6, width: 25 },
      details: { group: 1, category: gral, order: 14 }
    },
    {
      title: lang.sessionDate,
      field: "sessionDate",
      format: "date",
      details: { group: 1, category: gral, order: 15 }
    },
    {
      title: lang.tcod,
      field: "commerceTCOD",
      excel: { order: 10, width: 10 },
      details: { group: 2, category: come, order: 16 },
      table: { order: 13 }
    },
    {
      title: lang.commerceCode,
      field: "commerceCodeNumber",
      excel: { order: 13, width: 19 },
      details: { group: 2, category: come, order: 17 }
    },
    {
      title: lang.device,
      field: "pinPad",
      excel: { order: 32, width: 12 },
      details: { group: 2, category: come, order: 18 },
      table: { order: 16, sortType: "number" }
    },
    {
      title: lang.company,
      field: "company",
      excel: { order: 33, width: 12 },
      details: { group: 2, category: come, order: 18 },
      table: { order: 17 }
    },
    {
      title: lang.entryMode,
      field: "",
      format: (item: DataItem) => `${item.dataEntryText} (${item.dataEntryCode})`,
      details: { group: 2, category: come, order: 19 }
    },
    {
      title: lang.titular,
      field: "cardHolder",
      excel: { order: 23, width: 19 },
      details: { group: 2, category: card, order: 20 }
    },
    {
      title: lang.pan,
      field: "veryHiddenCardNumber",
      excel: { order: 16, width: 19 },
      details: { group: 2, category: card, order: 21 },
      table: { order: 10 }
    },
    {
      title: lang.cardBin,
      field: "binNumber",
      excel: { order: 15 },
      details: { group: 2, category: card, order: 22 }
    },
    {
      title: lang.card,
      field: "cardTypeCode",
      excel: { order: 14 },
      details: { group: 2, category: card, order: 23 },
      table: {
        order: 9,
        filter: {
          filterField: "cardTypeCode",
          list: {
            multi: true,
            values: Object.keys(CARD_TYPES),
            options: Object.keys(CARD_TYPES).map((k) => ({
              value: k,
              text: CARD_TYPES[k as CardTypeCode]
            }))
          }
        }
      }
    },
    {
      title: lang.cardIssuer,
      field: "issuerNameAndCountry",
      excel: { order: 29, width: 18 },
      details: { group: 2, category: card, order: 24 }
    },
    {
      title: lang.companyCard,
      field: "isCorporate",
      format: "boolSiNo",
      excel: { order: 31, width: 17 },
      details: { group: 2, category: card, order: 25 }
    },
    {
      title: lang.creditoOrDebit,
      field: "creditOrDebit",
      excel: { order: 24 },
      details: { group: 2, category: card, order: 26 }
    },
    {
      title: lang.server,
      field: "processingServer",
      excel: { order: 25 },
      details: { group: 2, category: proc, order: 27 }
    },
    {
      title: lang.processStatus,
      field: "processingStatus",
      excel: { order: 26 },
      details: { group: 2, category: proc, order: 28 }
    },
    {
      title: lang.authorizationCenter,
      field: "hcpName",
      excel: { order: 20, width: 17 },
      details: { group: 2, category: proc, order: 29 }
    },
    {
      title: lang.merchantBank,
      field: "bankName",
      excel: { order: 27 },
      details: { group: 2, category: proc, order: 30 }
    },
    {
      title: lang.t1,
      field: "t1",
      excel: { order: 21, width: 10 },
      details: { group: 2, category: proc, order: 31 }
    },
    {
      title: lang.t2,
      field: "t2",
      excel: { order: 22, width: 10 },
      details: { group: 2, category: proc, order: 32 }
    },
    {
      title: lang.previousTransaction,
      field: "previousTransactionID",
      excel: { order: 32, width: 18 },
      details: { group: 2, category: proc, order: 33 }
    }
  ];
}
